import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export const ImpersonationBanner = () => {
  const { impersonation, account, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  if (!impersonation?.active) return null;
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 bg-amber-500 px-4 py-2 text-[12px] font-semibold text-slate-900"
      data-testid="impersonation-banner"
    >
      <span>
        Je bekijkt ImDash als <strong>{account?.name}</strong> — ondersteuningsmodus, alle acties
        worden gelogd.
      </span>
      <button
        type="button"
        data-testid="stop-impersonation-button"
        onClick={async () => {
          try {
            await stopImpersonation();
            navigate("/admin");
          } catch (e) {
            toast.error(apiError(e));
          }
        }}
        className="rounded-sm bg-slate-900 px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-slate-800"
      >
        Terug naar Master Admin
      </button>
    </div>
  );
};

export const UserMenu = () => {
  const { user, account, isMaster, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const label = isMaster ? "ImDash Master" : account?.subscription?.plan_code === "imdash_pro"
    ? "ImDash Pro"
    : account?.name || "ImDash";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="user-menu-button"
        className="flex items-center gap-2 rounded-sm border border-slate-200 px-2.5 py-1.5 text-left transition-colors duration-150 hover:bg-slate-50"
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-white">
          {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
        </span>
        <span className="hidden leading-tight sm:block">
          <span className="block text-[12px] font-semibold text-slate-900">
            {user?.name || user?.email}
          </span>
          <span className="block text-[10px] font-semibold uppercase tracking-[0.08em] text-blue-700">
            {label}
          </span>
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
      </button>
      {open && (
        <div
          className="absolute right-0 z-40 mt-1 w-52 rounded-sm border border-slate-200 bg-white py-1 shadow-lg"
          data-testid="user-menu"
        >
          <div className="border-b border-slate-100 px-3 py-2">
            <div className="text-[12px] font-semibold text-slate-900">{account?.name}</div>
            <div className="text-[11px] text-slate-500">{user?.email}</div>
          </div>
          {isMaster && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/admin");
              }}
              data-testid="user-menu-admin"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-slate-700 hover:bg-slate-50"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Master Admin
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/instellingen");
            }}
            data-testid="user-menu-settings"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-slate-700 hover:bg-slate-50"
          >
            <User className="h-3.5 w-3.5" /> Mijn account & instellingen
          </button>
          <button
            type="button"
            onClick={async () => {
              setOpen(false);
              await logout();
              navigate("/login");
            }}
            data-testid="user-menu-logout"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-slate-700 hover:bg-slate-50"
          >
            <LogOut className="h-3.5 w-3.5" /> Uitloggen
          </button>
        </div>
      )}
    </div>
  );
};
